import jsonFormData from "json-form-data";
import { ClientHandlingErrorReason, ClientHandlingErrorStatusCode } from "./contract-map.js";
import { createEventSourceEvents } from "./sse-parse.js";
const DEFAULT_ACCEPT_HEADER = "application/json, */*;q=0.1";
/**
 * Create a full type-safe fetch client from multiple API endpoints defined by contracts:
 *
 * - [x] Isomorphic (works in Node.js and browser).
 * - [x] Type-safe error handling without try/catch at call sites through discriminated union.
 * - [x] Timeouts (per request & per attempt) with AbortController.
 *     - [x] Pass `AbortSignal` to the request in combination with the timeout.
 *     - [x] EventSource (`text/event-stream`): timeout covers the handshake only; the open stream follows the caller's `signal`.
 * - [x] Retry mechanism with attempt count and delay.
 *     - [x] Default retry on timeout, network errors and these statuses: 408, 409, 425, 429, 500, 502, 503, 504
 *     - [ ] Respect Retry-After (seconds or HTTP-date).
 *     - [ ] Configurable retries with exponential backoff + jitter.
 * - [x] Uploads / Multipart requests.
 *
 * ### Error handling
 *
 * Due to the fact that nor TypeScript nor JavaScript provide a type-safe way to handle errors,
 * all errors (non-2xx status codes) and network errors are handled by the client and returned
 * as a discriminated union:
 *
 * - No try/catch at call sites
 * - Fully typed success/error flows
 * - Easy exhaustive handling with switch/if guards
 *
 * See the `README.md` for more examples.
 */ function createFetchClient({ baseUrl, fetchFn = fetch, headers: baseHeaders, timeout: baseTimeout, retry: baseRetry, version, ...baseRequestInit }) {
    const run = async (path, [payload])=>{
        const { headers: requestHeaders = {}, timeout: requestTimeout, retry: requestRetry, signal: requestSignal, ...requestInit } = payload;
        const originalRequestParams = "params" in payload && payload.params !== undefined && typeof payload.params === "object" && payload.params !== null ? payload.params : {};
        const requestBody = "request" in payload ? payload.request : undefined;
        const method = path.pop()?.toUpperCase();
        // The `pathToUrlString` removes all used params which are interpolated to the path, therefore we do not want to touch the original params object.
        const requestParams = {
            ...originalRequestParams
        };
        const url = new URL(`${version === null || version === undefined ? "" : `${version}/`}${pathToUrlString(path, requestParams)}`, baseUrl);
        const { searchParams } = url;
        const timeout = requestTimeout ?? baseTimeout;
        const retryOptions = {
            count: requestRetry?.count ?? baseRetry?.count ?? 0,
            delay: requestRetry?.delay ?? baseRetry?.delay ?? 500,
            statusCodes: requestRetry?.statusCodes ?? baseRetry?.statusCodes ?? [
                408,
                409,
                425,
                429,
                500,
                502,
                503,
                504
            ],
            clientHandlingErrorStatuses: requestRetry?.clientHandlingErrorStatuses ?? baseRetry?.clientHandlingErrorStatuses ?? [
                ClientHandlingErrorReason.Network,
                ClientHandlingErrorReason.Timeout
            ]
        };
        const { count: retryCount, delay: retryDelay } = retryOptions;
        let attempt = 0;
        for (const [key, value] of Object.entries(requestParams)){
            if (value !== undefined) {
                searchParams.set(key, String(value));
            }
        }
        let foundFile = false;
        if (requestBody) {
            JSON.stringify(requestBody, (key, value)=>{
                if (typeof value === "object" && [
                    "File",
                    "Blob"
                ].includes(value?.constructor?.name)) {
                    foundFile = true;
                    return value.name;
                }
                return value;
            });
        }
        const headers = new Headers({
            Accept: DEFAULT_ACCEPT_HEADER,
            ...baseHeaders,
            ...requestHeaders
        });
        // Only set Content-Type for JSON requests. For FormData, let the browser set it with the boundary.
        // Only set Content-Type when there's a body to send.
        // "Accept"-header defines the response body, and "Content-Type" defines the request body.
        // Don't set it for GET requests - some servers (like Browserless) reject it.
        if (requestBody !== undefined && !foundFile && !headers.has("content-type")) {
            headers.set("Content-Type", "application/json");
        }
        const singleRun = async ()=>{
            // Local controller so REST `timeout` covers the handshake / JSON body only.
            // For SSE we clear the timer after headers so the open stream is not killed.
            const runAbort = new AbortController();
            const onExternalAbort = ()=>runAbort.abort();
            requestSignal?.addEventListener("abort", onExternalAbort, {
                once: true
            });
            let timedOut = false;
            let timeoutId;
            if (timeout) {
                timeoutId = setTimeout(()=>{
                    timedOut = true;
                    runAbort.abort();
                }, timeout);
            }
            const clearHandshakeTimeout = ()=>{
                if (timeoutId !== undefined) {
                    clearTimeout(timeoutId);
                    timeoutId = undefined;
                }
            };
            let responseInstance;
            try {
                responseInstance = await fetchFn(url.toString(), {
                    ...baseRequestInit,
                    ...requestInit,
                    method,
                    headers,
                    body: requestBody ? foundFile ? jsonFormData(requestBody, {}) : JSON.stringify(requestBody) : undefined,
                    signal: runAbort.signal
                });
                const { headers: responseHeaders } = responseInstance;
                const responseContentType = responseHeaders.get("Content-Type");
                if (responseContentType?.includes("text/event-stream")) {
                    // Stream lifetime follows the caller's AbortSignal only — not the REST timeout.
                    clearHandshakeTimeout();
                    requestSignal?.removeEventListener("abort", onExternalAbort);
                    if (!responseInstance.body) {
                        return {
                            status: ClientHandlingErrorStatusCode,
                            reason: ClientHandlingErrorReason.ParseError,
                            error: new Error("SSE response has no body"),
                            responseInstance
                        };
                    }
                    return {
                        status: responseInstance.status,
                        code: responseInstance.status,
                        events: createEventSourceEvents(responseInstance.body, requestSignal),
                        responseInstance
                    };
                }
                let responseJson;
                // const responseContentLength = +(responseHeaders.get("Content-Length") ?? 0);
                // if (responseContentLength > 0) { -> not always given, e.g. when response is chunked: https://stackoverflow.com/a/11375745/5506547
                if (responseContentType?.includes("application/json")) {
                    try {
                        responseJson = await responseInstance.clone().json();
                    } catch (e) {
                        return {
                            status: ClientHandlingErrorStatusCode,
                            reason: ClientHandlingErrorReason.ParseError,
                            error: e
                        };
                    }
                }
                // }
                return {
                    status: responseInstance.status,
                    code: responseInstance.status,
                    response: responseJson,
                    responseInstance
                };
            } catch (e) {
                if (e instanceof Error && e.name === "AbortError") {
                    return {
                        status: ClientHandlingErrorStatusCode,
                        reason: timedOut ? ClientHandlingErrorReason.Timeout : ClientHandlingErrorReason.Abort,
                        error: e,
                        responseInstance
                    };
                }
                return {
                    status: ClientHandlingErrorStatusCode,
                    reason: ClientHandlingErrorReason.Network,
                    error: e,
                    responseInstance
                };
            } finally{
                clearHandshakeTimeout();
                requestSignal?.removeEventListener("abort", onExternalAbort);
            }
        };
        // Retry mechanism
        let doRetry = true;
        let result;
        while(doRetry){
            if (attempt > 0 && retryDelay > 0) {
                await new Promise((resolve)=>setAbortableTimeout(resolve, resolve, retryDelay, requestSignal));
            }
            // Special case: If the consumer aborted the request, we should not retry
            if (requestSignal?.aborted) {
                if (!result) {
                    result = {
                        status: ClientHandlingErrorStatusCode,
                        reason: ClientHandlingErrorReason.Abort,
                        error: new Error("Request aborted")
                    };
                }
                break;
            }
            result = await singleRun();
            if (retryCount > 0 && (retryOptions.statusCodes.includes(result.status) || result.status === ClientHandlingErrorStatusCode && retryOptions.clientHandlingErrorStatuses.includes(result.reason))) {
                if (attempt >= retryCount) {
                    doRetry = false;
                    result = {
                        status: ClientHandlingErrorStatusCode,
                        reason: ClientHandlingErrorReason.RetryLimitReached,
                        error: new Error("Retry limit reached")
                    };
                    break;
                }
                doRetry = true;
                attempt++;
            } else {
                doRetry = false;
            }
        }
        return result;
    };
    return createProxy(run);
}
/**
 * Create a URL string from a path array. The path is the camelCase'd, slash-stripped path of the contract.
 */ const pathToUrlString = (path, params)=>{
    return path.map((p)=>{
        if (p.startsWith(":")) {
            const paramKey = p.slice(1);
            const value = params[paramKey];
            delete params[paramKey];
            return value;
        }
        return p.replace(/([A-Z])/g, "-$1").toLowerCase();
    }).join("/");
};
/**
 * Set a timeout that can be aborted by an AbortSignal.
 *
 * @see https://www.bennadel.com/blog/4195-using-abortcontroller-to-debounce-settimeout-calls-in-javascript.htm
 */ const setAbortableTimeout = (onSuccess, onAbort, ms, signal)=>{
    signal?.addEventListener("abort", handleAbort, {
        once: true
    });
    const internalTimer = setTimeout(internalCallback, ms);
    function internalCallback() {
        signal?.removeEventListener("abort", handleAbort);
        onSuccess();
    }
    function handleAbort() {
        clearTimeout(internalTimer);
        onAbort();
    }
};
/**
 * Create a callable object where any nested path is valid.
 * When that path is called, we invoke `handler(pathArray, argsArray)`.
 */ const createProxy = (handler)=>{
    const build = (path = [])=>new Proxy(()=>{}, {
            get (_t, prop) {
                // Return undefined for 'then' to prevent being treated as a Promise
                if (prop === "then") {
                    return undefined;
                }
                // Handle stringification and inspection methods
                const stringMethods = [
                    "toString",
                    "valueOf",
                    Symbol.toPrimitive
                ];
                if (stringMethods.includes(prop)) {
                    return ()=>path.join(".");
                }
                // Handle Node.js inspection
                const inspectSymbols = [
                    "inspect",
                    Symbol.for("nodejs.util.inspect.custom")
                ];
                if (inspectSymbols.includes(prop)) {
                    return ()=>`ProxyPath(${path.join(".")})`;
                }
                // Set object type tag
                if (prop === Symbol.toStringTag) {
                    return "Function";
                }
                // Any other property extends the path
                return build(path.concat(String(prop)));
            },
            apply (_t, _thisArg, args) {
                // Call the user handler with the collected path + args
                return handler([
                    ...path
                ], Array.from(args));
            }
        });
    return build();
};
export { createFetchClient, createProxy };
